import { oc } from "@orpc/contract";
import { z } from "zod";

/**
 * Holders (`holder`, CONTEXT.md) in the admin: the kinds of device a refill goes on ("bekende
 * XXL-wissers", "plumeaustelen"), by the name the size label uses. A brand may be named, never as a
 * comparison. Which variant fits which holder is set in the product editor
 * (`product.setVariantFits`). Every write answers the whole list, in order.
 */

export const Holder = z.object({
	id: z.string(),
	name: z.string(),
	/** One of them, as the shop says it to a customer who has one: "XXL-wisser"; `null`: not filled in. */
	singularName: z.string().nullable(),
	/** The size of the holder's foot, as typed: "42×12 cm"; `null`: not filled in, the shop leaves it out. */
	footSize: z.string().nullable(),
	/** A short tip on how to measure, for the fit guide and the wisserpagina; `null`: none. */
	measuringTip: z.string().nullable(),
	/** 0 is first. */
	position: z.number().int(),
	/** How many variants say "past op" or "niet voor" this holder. */
	variantCount: z.number().int(),
	/** How many groups are this holder's wisserpagina. */
	categoryCount: z.number().int(),
	createdAt: z.iso.datetime(),
	updatedAt: z.iso.datetime(),
});
export type Holder = z.infer<typeof Holder>;

/**
 * A holder's name, as it reads in the size label after "past op" or "niet voor", and its singular
 * name ("Dit past niet op je XXL-wisser"), its foot size and its measuring tip. For each of the
 * optional three: empty clears it; left out, a new holder has none and an edit keeps the one it had.
 */
export const HolderFields = z.object({
	name: z.string().trim().min(1).max(100),
	singularName: z.string().trim().max(100).optional(),
	footSize: z.string().trim().max(40).optional(),
	measuringTip: z.string().trim().max(300).optional(),
});
export type HolderFields = z.infer<typeof HolderFields>;

/** The new order: the id of every holder, each once, first to last. */
export const HolderOrder = z.object({
	ids: z
		.array(z.string())
		.max(1000)
		.refine((ids) => new Set(ids).size === ids.length, "each holder once"),
});

const ById = z.object({ holderId: z.string().min(1) });

export const HolderContract = {
	list: oc.route({ method: "GET", path: "/admin/holders" }).output(z.array(Holder)),
	/** Errors: `HolderNameTaken` (409). */
	create: oc.route({ method: "POST", path: "/admin/holders" }).input(HolderFields).output(z.array(Holder)),
	/** Errors: `HolderNotFound` (404), `HolderNameTaken` (409). */
	update: oc
		.route({ method: "PUT", path: "/admin/holders/{holderId}" })
		.input(HolderFields.extend(ById.shape))
		.output(z.array(Holder)),
	/**
	 * Deletes the holder and every fit stated for it, and unlinks its groups (they become category
	 * pages), in one batch. Errors: `HolderNotFound` (404).
	 */
	delete: oc
		.route({ method: "DELETE", path: "/admin/holders/{holderId}" })
		.input(ById)
		.output(z.array(Holder)),
	/** Errors: `HolderOrderOutdated` (409). */
	reorder: oc
		.route({ method: "PUT", path: "/admin/holders/order" })
		.input(HolderOrder)
		.output(z.array(Holder)),
};
